// Enums
export enum Quality {
  AGAIN = "again",
  HARD = "hard",
  GOOD = "good",
  EASY = "easy"
}

// Flashcard Models
export interface FlashcardBase {
  front_text: string
  back_text: string
  reading?: string
  meaning?: string
  examples: string[]  // Multiple example sentences
  tags: string[]
  difficulty?: string
  card_type?: string
  deck_id?: string  // Optional deck association
}

export interface Flashcard extends FlashcardBase {
  card_id: string
  created_at: string
  updated_at: string
}

export interface FlashcardCreate extends FlashcardBase {
  // Additional fields for creation
}

export interface FlashcardUpdate {
  front_text?: string
  back_text?: string
  reading?: string
  meaning?: string
  examples?: string[]
  tags?: string[]
}

// Deck Models
export interface DeckBase {
  name: string
  description?: string
  category?: string
}

export interface Deck extends DeckBase {
  deck_id: string
  user_id: string
  card_count: number
  created_at: string
  updated_at: string
}

export interface DeckCreate extends DeckBase {
  // Additional fields for creation
}

export interface DeckUpdate {
  name?: string
  description?: string
  category?: string
}

// User Models
export interface User {
  user_id: string
  username: string
  email: string
  created_at: string
  updated_at: string
}

export interface UserUpdate {
  username?: string
  email?: string
  password?: string
}

// User Progress Models
export interface UserProgressBase {
  card_id: string
  ease_factor: number
  interval: number
  repetitions: number
  last_review?: string
  next_review: string
}

export interface UserProgress extends UserProgressBase {
  progress_id: string
  user_id: string
  created_at: string
  updated_at: string
}

export interface UserProgressUpdate {
  ease_factor?: number
  interval?: number
  repetitions?: number
  last_review?: string
  next_review?: string
}

// Flashcard Progress (alias for UserProgress, used in FlashcardsContext)
export interface FlashcardProgress extends UserProgressBase {
  user_id: string
  created_at: string
  updated_at: string
}

// Authentication Models
export interface OidcConfig {
  issuer: string
  client_id: string | null
  jwks_uri: string
}

// Request/Response Models
export interface ReviewRequest {
  card_id: string
  quality: Quality
}

export interface ReviewResponse {
  card: Flashcard
  progress: UserProgress
  next_review: SpacedRepetitionResponse
}

export interface SpacedRepetitionResponse {
  card_id: string
  ease_factor: number
  interval: number
  repetitions: number
  next_review: string
  quality: Quality
}

export interface StatisticsResponse {
  total_cards: number
  due_cards: number
  new_cards: number
  reviewed_today: number
  average_retention: number
  streak_days: number
  last_review_date?: string
}

// API Response Wrapper
export interface ApiResponse<T = any> {
  success?: boolean
  data?: T
  error?: string
  message?: string
}

// Learning Session Types
export type LearningQuality = 0 | 1 | 2 | 3;  // AGAIN, HARD, GOOD, EASY

export interface LearningRatingRequest {
  card_id: string;
  quality: LearningQuality;
}

export interface UserLearningRating {
  user_id: string;
  card_id: string;
  quality: LearningQuality;
  rated_at: string;
  last_updated: string;
}

export interface LearningRatingResponse {
  rating: UserLearningRating;
}

export interface UnreviewedCardsResponse {
  cards: Flashcard[];
  total: number;
  filters_applied?: {
    tags?: string[];
    count: number;
  };
  message?: string;
}

// BCCWJ Models
export type BCCWJMemoryStatus = 1 | 2 | 3 | 4

export interface BCCWJWord {
  id: string
  lemma: string
  lform?: string
  hiragana?: string
  pos?: string
  wtype?: string
  frequency?: number
  pmw?: number
  rank?: number
  status?: BCCWJMemoryStatus | null
  check_count?: number | null
}

export interface BCCWJWordSearchResponse {
  words: BCCWJWord[]
  total: number
  limit: number
  offset: number
}

export interface BCCWJStats {
  total_words: number
  status_counts: Record<string, number>
}

// Triage Models
export interface TriageWordResponse {
  word: BCCWJWord | null
  total_remaining: number
}

export interface BCCWJWordStatusResponse {
  bccwj_word_id: string
  status: BCCWJMemoryStatus
  check_count: number
  updated_at: string
}
